/*
 * Copyright 2026 Sony Corporation
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import type { RealtimeEvent } from '@ips-device-verse/twin-service/schema/realtime';

import type { SceneOp } from '@ips-device-verse/twin-service/schema/scene-doc';

import { createAgentClient, type AgentClient } from '../services/agent-client';
import type { SceneDigest } from '../scene-doc/digest';
import { createRealtimeClient, type RealtimeStatus } from '../services/realtime-client';
import { useAuth } from '../contexts/auth-context';

export interface UseSceneAgentOptions {
  /** The world whose socket carries the conversation. */
  worldId: string | undefined;
}

export interface UseSceneAgentResult {
  requestOps: AgentClient<SceneOp, SceneDigest>['requestOps'];
  newThread: () => void;
  status: RealtimeStatus;
  /** The turn being written, so the panel can show it arriving rather than appearing. */
  streaming: string | undefined;
}

/**
 * The Scene page's end of the agent (devplan §7.1).
 *
 * A scene belongs to a world, so this is the world's socket — the same one the
 * device agent uses. The turn is still a reply to this tab rather than an event
 * the world hears about; only the transport is shared.
 */
export const useSceneAgent = ({ worldId }: UseSceneAgentOptions): UseSceneAgentResult => {
  const { config, auth } = useAuth();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const [streaming, setStreaming] = useState<string>();
  const listeners = useRef(new Set<(event: RealtimeEvent) => void>());
  const clientRef = useRef<AgentClient<SceneOp, SceneDigest>>(null);

  useEffect(() => {
    if (!worldId) {
      return;
    }

    const socket = createRealtimeClient({
      url: config.ws_url,
      worldId,
      getIdToken: auth.getIdToken,
      onStatus: setStatus,
      onEvent: (event) => {
        for (const listener of listeners.current) {
          listener(event);
        }
      },
    });

    clientRef.current = createAgentClient<SceneOp, SceneDigest>({
      socket,
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => listeners.current.delete(listener);
      },
      onDelta: setStreaming,
    });

    return () => {
      clientRef.current = null;
      socket.close();
    };
  }, [worldId, config.ws_url, auth]);

  return useMemo(
    () => ({
      requestOps: async (prompt, digest) => {
        setStreaming('');
        try {
          if (!clientRef.current) {
            throw new Error('The assistant is not connected yet.');
          }
          return await clientRef.current.requestOps(prompt, digest);
        } finally {
          setStreaming(undefined);
        }
      },
      newThread: () => clientRef.current?.newThread(),
      status,
      streaming,
    }),
    [status, streaming]
  );
};
